import { createContext, FC, useCallback, useContext, useEffect, useState } from "react";
import { AutocompleteSuggestions } from "../types/Autocomplete";
import { useRequest } from "../api";
import {
  getAutocompleteSuggestions,
  GetAutocompleteSuggestionsResponse,
} from "../api/actions/getAutocompleteSuggestions";
import { usePushErrorLog } from "../hooks/usePushErrorLog";

interface AutocompleteSuggestionsContextValue {
  autocompleteSuggestions: AutocompleteSuggestions;
  isFetching: boolean;
  fetchAutocompleteSuggestions: () => void;
}

const DEFAULT_CONTEXT_VALUE: AutocompleteSuggestionsContextValue = {
  autocompleteSuggestions: {
    popularSuggestions: {
      flightsDepartureAirport: [],
      flightsDestinationAirport: [],
      hotelDestination: [],
      packageDepartureAirport: [],
      packageDestination: [],
    },
    popularSuggestionsPerDeparture: {
      flightDestinationAirportPerDeparture: {},
      packageDestinationPerDeparture: {},
    },
    recentSuggestions: {
      flightsDepartureAirport: [],
      flightsDestinationAirport: [],
      hotelDestination: [],
      packageDepartureAirport: [],
      packageDestination: [],
    },
  },
  isFetching: false,
  fetchAutocompleteSuggestions: () => {},
};

const AutocompleteSuggestionsContext =
  createContext<AutocompleteSuggestionsContextValue>(DEFAULT_CONTEXT_VALUE);

export const AutocompleteSuggestionsContextProvider: FC = ({ children }) => {
  const request = useRequest();
  const logError = usePushErrorLog();
  const [isFetching, setIsFetching] = useState(false);
  const [popularSuggestions, setPopularSuggestions] = useState(
    DEFAULT_CONTEXT_VALUE.autocompleteSuggestions.popularSuggestions
  );
  const [popularSuggestionsPerDeparture, setPopularSuggestionsPerDeparture] = useState(
    DEFAULT_CONTEXT_VALUE.autocompleteSuggestions.popularSuggestionsPerDeparture
  );
  const [recentSuggestions, setRecentSuggestions] = useState(
    DEFAULT_CONTEXT_VALUE.autocompleteSuggestions.recentSuggestions
  );

  const fetchAutocompleteSuggestions = useCallback(async () => {
    setIsFetching(true);
    try {
      const data = await request<GetAutocompleteSuggestionsResponse>(getAutocompleteSuggestions());
      setPopularSuggestions(data.popularSuggestions);
      setPopularSuggestionsPerDeparture(data.popularSuggestionsPerDeparture);
      setRecentSuggestions(data.recentSuggestions);
    } catch (error) {
      logError(error, {
        customMessage: "Error during fetching airports autocomplete suggestions",
      });
    } finally {
      setIsFetching(false);
    }
  }, [logError, request]);

  useEffect(() => {
    fetchAutocompleteSuggestions();
  }, [fetchAutocompleteSuggestions]);

  return (
    <AutocompleteSuggestionsContext.Provider
      value={{
        autocompleteSuggestions: {
          popularSuggestions,
          popularSuggestionsPerDeparture,
          recentSuggestions,
        },
        isFetching,
        fetchAutocompleteSuggestions,
      }}
    >
      {children}
    </AutocompleteSuggestionsContext.Provider>
  );
};

export const useAutocompleteSuggestionsContext = () => useContext(AutocompleteSuggestionsContext);
